// Server-safe hub copy: FAQ + comparison data for the season hub pages.
// Imported by both page.tsx (server, for FAQSchema) and content.tsx (client).

export interface FAQ {
  question: string;
  answer: string;
}

interface SubseasonCopy {
  displayName: string;
  summary: string;
  undertone: string;
  depth: string;
  chroma: string;
  bestIf: string;
}

interface SeasonHubCopy {
  subseasons: {
    [key: string]: SubseasonCopy;
  };
  faqs: FAQ[];
}

// Original copy for each season hub page — subseason summaries, comparison
// table values, and FAQ answers. Kept separate from seasonMetadata (which is
export const seasonHubData: { [key: string]: SeasonHubCopy } = {
  SPRING: {
    subseasons: {
      LIGHT: {
        displayName: "Light Spring",
        summary:
          "The palest, most delicate of the three — pastel-leaning warmth with a lot of white mixed into every color.",
        undertone: "Warm, golden",
        depth: "Light",
        chroma: "Medium-clear",
        bestIf: "Your coloring is fair with light hair and light eyes",
      },
      TRUE: {
        displayName: "True Spring",
        summary:
          "The warmest of the spring group — pure golden undertones with medium depth and clean, saturated color.",
        undertone: "Warm, golden (purest warmth of the three)",
        depth: "Light-medium",
        chroma: "Clear, saturated",
        bestIf: "You read as warm before anything else — golden skin, warm brown or auburn hair",
      },
      BRIGHT: {
        displayName: "Bright Spring",
        summary:
          "The most vivid subseason in the family — warm undertone but the clarity and saturation of a Winter.",
        undertone: "Warm, but only slightly — clarity dominates",
        depth: "Medium",
        chroma: "High, vivid",
        bestIf: "Muted colors wash you out but pastels also look flat — you need saturation",
      },
    },
    faqs: [
      {
        question: "What is a Spring color palette?",
        answer:
          "Spring covers three subseasons — Light, True, and Bright — that share a warm, golden undertone but differ in depth and chroma. Light Spring is the palest and softest, True Spring sits in the middle with the purest warmth, and Bright Spring pushes into high-saturation, almost Winter-level vividness while staying warm.",
      },
      {
        question: "How do I know which Spring subseason I am?",
        answer:
          "Check depth first: if your hair and skin are both light, you're likely Light Spring. If your coloring reads warm above all else — golden skin, warm brown hair — you're probably True Spring. If muted colors make you look flat but you're clearly warm-toned, that's the signature of Bright Spring. AI draping is more reliable than eyeballing a chart because sister seasons look nearly identical in photos.",
      },
      {
        question: "Can a Spring wear black?",
        answer:
          "Black isn't in any Spring palette — it's too cool and too heavy for a warm, light-to-medium season and tends to overpower the face. Warm charcoal, chocolate brown, or navy do the same job (grounding an outfit) without the drain. Bright Spring can get away with black closer to the face if it's broken up with a warm accent color.",
      },
      {
        question: "What's the difference between True Spring and Bright Spring?",
        answer:
          "Both are warm, but True Spring is defined by undertone purity — it's the most unmistakably golden of the three. Bright Spring is defined by contrast and saturation instead; it can wear more intense, high-chroma color than True Spring without looking costume-y, because its coloring has more built-in contrast to match.",
      },
    ],
  },
  SUMMER: {
    subseasons: {
      LIGHT: {
        displayName: "Light Summer",
        summary:
          "Soft, cool, and pale — the lightest Summer, built on powdery pastels with a gentle blue-grey undertone.",
        undertone: "Cool, slightly muted",
        depth: "Light",
        chroma: "Low-medium, soft",
        bestIf: "You're fair-skinned with light ash or blonde hair and low natural contrast",
      },
      TRUE: {
        displayName: "True Summer",
        summary:
          "The coolest of the three — pure blue undertone with medium depth and a soft, dusty finish on every color.",
        undertone: "Cool, purest blue of the three",
        depth: "Medium",
        chroma: "Low-medium, dusty",
        bestIf: "You read as cool before anything else and warm colors look sallow on you",
      },
      COOL: {
        displayName: "Soft Summer",
        summary:
          "The most muted subseason in the family — cool undertone but the lowest chroma, favoring greyed-down, dusty color over anything saturated.",
        undertone: "Cool, but muting dominates over blueness",
        depth: "Medium",
        chroma: "Very low, greyed",
        bestIf: "Bright, saturated colors overwhelm you but you're not warm enough for Soft Autumn",
      },
    },
    faqs: [
      {
        question: "What is a Summer color palette?",
        answer:
          "Summer covers three subseasons — Light, True, and Soft (sometimes called Cool Summer) — that all share a cool undertone but differ in depth and how muted the colors are. Light Summer is palest and softest, True Summer is the purest blue-toned of the three, and Soft Summer is the most muted, favoring dusty, greyed color over anything saturated.",
      },
      {
        question: "How do I know which Summer subseason I am?",
        answer:
          "Start with contrast: low natural contrast between skin, hair, and eyes usually points to Light or Soft Summer, while a bit more definition points to True Summer. If you're unsure whether you're cool-and-clear or cool-and-muted, that's the True vs. Soft split — muting is the deciding factor for Soft Summer. Photos under different lighting can flip your read, which is why AI draping beats a self-typing chart.",
      },
      {
        question: "Can a Summer wear black?",
        answer:
          "Technically Summer is cool enough for black's undertone, but black's intensity is too harsh for Summer's soft, low-chroma palette — it tends to look like a hole in the outfit rather than a neutral. Charcoal grey or soft navy give the same grounding effect without fighting the softness that defines this season.",
      },
      {
        question: "Why is Soft Summer sometimes called Cool Summer?",
        answer:
          "Different color analysis systems name the same subseason differently — some call it Cool Summer because it sits at the cooler end of the Summer group, others call it Soft Summer because muting, not coolness, is its defining trait. On this site we use Soft Summer to keep it distinct from True Summer, which is actually the coolest (most blue) of the three.",
      },
    ],
  },
  AUTUMN: {
    subseasons: {
      SOFT: {
        displayName: "Soft Autumn",
        summary:
          "The most muted subseason in the family — warm-leaning but greyed down, closer to Soft Summer than to True Autumn in intensity.",
        undertone: "Warm, but muted — can lean neutral",
        depth: "Light-medium",
        chroma: "Very low, greyed",
        bestIf: "Saturated warm colors look costume-y on you but pure cool colors look wrong too",
      },
      TRUE: {
        displayName: "True Autumn",
        summary:
          "The warmest of the three — deep, earthy golden undertone with medium-to-full saturation and no grey mixed in.",
        undertone: "Warm, purest gold of the three",
        depth: "Medium",
        chroma: "Medium, rich",
        bestIf: "You read as warm before anything else — golden or olive skin, warm brown or auburn hair",
      },
      DEEP: {
        displayName: "Deep Autumn",
        summary:
          "The darkest and richest subseason — warm undertone carried at high depth, closer to jewel tones than earth tones.",
        undertone: "Warm, but depth dominates the read",
        depth: "Deep",
        chroma: "Medium-high, rich",
        bestIf: "Your natural coloring is dark — deep brown or black hair, deeper skin — and pale colors wash you out",
      },
    },
    faqs: [
      {
        question: "What is an Autumn color palette?",
        answer:
          "Autumn covers three subseasons — Soft, True, and Deep — that share a warm undertone but differ mainly in depth and saturation. Soft Autumn is the most muted and greyed-down, True Autumn is the purest gold with medium richness, and Deep Autumn carries the same warmth at much higher depth, closer to jewel tones than earth tones.",
      },
      {
        question: "How do I know which Autumn subseason I am?",
        answer:
          "Depth is the first signal: if your hair and skin are both dark, look at Deep Autumn first. If your coloring is warm but muted rather than rich — hair and skin closer in value, low contrast — that's more likely Soft Autumn. True Autumn sits in the middle: warm, medium depth, without the muting of Soft or the darkness of Deep. Because Soft Autumn and Soft Summer can look alike on paper, photo-based draping is worth doing before committing to either.",
      },
      {
        question: "Can an Autumn wear black?",
        answer:
          "Black is too cool and too stark for any Autumn subseason — it flattens warm undertones and reads as harsh next to Autumn's earthy palette. Deep Autumn can handle something close to black, like espresso brown or bittersweet, but true black is off the palette for all three.",
      },
      {
        question: "What's the difference between Soft Autumn and Deep Autumn?",
        answer:
          "They're on opposite ends of the same warm family. Soft Autumn is defined by low chroma — everything is muted and greyed down, even at lighter depth. Deep Autumn is defined by high depth — the colors can be much richer and more saturated because the person's natural coloring is dark enough to carry them without looking overdone.",
      },
    ],
  },
  WINTER: {
    subseasons: {
      COOL: {
        displayName: "Cool Winter",
        summary:
          "The iciest, clearest subseason — cool undertone with high contrast and bright, crisp color rather than deep or muted tones.",
        undertone: "Cool, clarity dominates",
        depth: "Medium",
        chroma: "High, icy-clear",
        bestIf: "You have high natural contrast but pastel and muted colors look chalky on you",
      },
      TRUE: {
        displayName: "True Winter",
        summary:
          "The purest cool undertone of the three — bold, saturated color with medium-to-deep intensity and zero warmth.",
        undertone: "Cool, purest blue of the three",
        depth: "Medium-deep",
        chroma: "High, saturated",
        bestIf: "You read as cool before anything else and any warm color looks jarring on you",
      },
      DEEP: {
        displayName: "Deep Winter",
        summary:
          "The darkest, most dramatic subseason — cool undertone carried at high depth, favoring rich jewel tones over icy brights.",
        undertone: "Cool, but depth dominates the read",
        depth: "Deep",
        chroma: "Medium-high, rich",
        bestIf: "Your natural coloring is dark — deep brown or black hair — and light icy colors wash you out",
      },
    },
    faqs: [
      {
        question: "What is a Winter color palette?",
        answer:
          "Winter covers three subseasons — Cool, True, and Deep — that all share a cool undertone but differ in what makes the color work. Cool Winter is defined by icy clarity, True Winter is the purest cool undertone with bold saturation, and Deep Winter carries the same coolness at much higher depth, favoring jewel tones over icy brights.",
      },
      {
        question: "How do I know which Winter subseason I am?",
        answer:
          "Depth and contrast are the two signals to check. If your hair and skin are both dark with strong contrast, start with Deep Winter. If you have high contrast but lighter overall coloring and pastels look chalky on you, that's more likely Cool Winter. True Winter sits in the middle — cool above all else, without the extra depth of Deep or the icy-specific clarity of Cool. Sister seasons in this group look close on a chart, so verifying with photo draping matters more here than in most seasons.",
      },
      {
        question: "Can a Winter wear black?",
        answer:
          "Yes — Winter is the one season family where pure black genuinely works, because the cool undertone and high natural contrast can carry it without looking flat. It's one of the few subseason groups where black functions as a real neutral rather than something to avoid.",
      },
      {
        question: "What's the difference between Cool Winter and True Winter?",
        answer:
          "Cool Winter is defined by clarity and icy brightness — its signature colors are crisp rather than deeply saturated. True Winter is defined by undertone purity — the most unmistakably cool-blue of the three — combined with bolder, more saturated color than Cool Winter typically wears.",
      },
    ],
  },
};

export const getSeasonFaqs = (season: string): FAQ[] => {
  return seasonHubData[season.toUpperCase()]?.faqs || [];
};
